import { FC } from 'react';
import { IoLockClosed, IoClose } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTenant } from '../../store/activeTenant.slice';
import { isBasicPlanValue } from '../../app-constants/subscription-plan.enum';
import { getProfileData } from '../../store/profile.slice';
import { Modal } from '@jadwalh/ui';
import { Flex, Button, ActionIcon } from '@mantine/core';
import { theme } from '@jadwalh/ui';
import styled from 'styled-components';

const StyledCloseButtonWrapper = styled.div`
  position: absolute;
  top: -20px;
  right: -8px;
  z-index: 10;

  button {
    border-radius: 8px;
  }

  @media (max-width: 640px) {
    right: -12px;
    top: -20px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 320px) {
    right: -12px;
    top: -20px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const IconWrapper = styled.div`
  color: #000000;
  margin-bottom: 8px;

  svg {
    width: 36px;
    height: 36px;

    @media (max-width: 640px) {
      width: 32px;
      height: 32px;
    }
  }
`;

const TitleText = styled.p`
  font-size: 18px;
  font-weight: 600;
  font-family: ${theme.fonts.Regular};
  text-align: center;
  margin: 0;
  color: #1e293b;
  line-height: 1.4;

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const DescriptionText = styled.p`
  font-size: 15px;
  font-weight: 400;
  font-family: ${theme.fonts.Regular};
  text-align: center;
  margin: 0;
  color: #64748b;
  line-height: 1.5;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const OKButtonWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
`;

interface BlockedFeatureStateProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
}

/**
 * BlockedFeatureState Component
 *
 * Shows a modal when a feature is blocked by subscription limits
 * Uses the standard Modal component from @jadwalh/ui for consistency
 *
 * Usage:
 * const { isBlocked } = useFeatureGate('can_use_workflows');
 * if (isBlocked) return <BlockedFeatureState title="..." description="..." onClose={() => ...} />;
 */
export const BlockedFeatureState: FC<BlockedFeatureStateProps> = ({
  title = 'هذه الميزة غير متاحة في خطتك الحالية',
  description = 'قم بالترقية للوصول إلى هذه الميزة والمزيد من الإمكانيات المتقدمة',
  icon,
  onClose,
}) => {
  const tenant = useSelector(getTenant);
  const profile = useSelector(getProfileData);
  const navigate = useNavigate();
  const isOwner = tenant?.is_owner === true;

  const planValue = Number(profile?.subscription_info?.plan_value);
  const isBasicPlan = isBasicPlanValue(planValue);
  const shouldShowUpgradeButton = isOwner && isBasicPlan;

  const handleUpgrade = () => {
    // Close parent modal if onClose is provided
    if (onClose) {
      onClose();
    }

    // Redirect to plans page
    navigate('/plans');
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // Navigate to root route if no onClose handler
      navigate('/');
    }
  };

  return (
    <Modal
      opened={true}
      size={480}
      onClose={handleClose}
      withCloseButton={false}
      padding="36px 24px"
      centered
      styles={{
        body: {
          position: 'relative',
          overflow: 'visible',
          '@media (max-width: 640px)': {
            padding: '24px',
          },
        },
        content: {
          overflow: 'visible',
          borderRadius: '20px',
        },
      }}
    >
      <div style={{ position: 'relative', overflow: 'visible' }}>
        {/* Custom Close Button */}
        <StyledCloseButtonWrapper>
          <ActionIcon
            onClick={handleClose}
            size="lg"
            variant="subtle"
            style={{
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <IoClose size={24} />
          </ActionIcon>
        </StyledCloseButtonWrapper>

        <Flex justify="center" align="center" direction="column" gap={24}>
          <IconWrapper>
            {icon || <IoLockClosed size={36} />}
          </IconWrapper>

          <TitleText>{title}</TitleText>

          <DescriptionText>{description}</DescriptionText>

          {shouldShowUpgradeButton ? (
            <Button
              onClick={handleUpgrade}
              fullWidth
              styles={{
                root: {
                  backgroundColor: '#000000',
                  color: 'white',
                  height: '30px',
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: '10px',
                  marginTop: '8px',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  '@media (max-width: 640px)': {
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    fontSize: '14px',
                  },
                },
              }}
            >
              عرض الباقات
            </Button>
          ) : !isOwner ? (
            <DescriptionText style={{ fontSize: '14px', fontWeight: '500' }}>
              يرجى التواصل مع مالك الحساب لترقية الخطة
            </DescriptionText>
          ) : null}

          {!shouldShowUpgradeButton && isOwner && (
            <OKButtonWrapper>
              <Button
                onClick={handleClose}
                fullWidth
                styles={{
                  root: {
                    backgroundColor: '#000000',
                    color: 'white',
                    height: '30px',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '10px',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    '@media (max-width: 640px)': {
                      width: '100%',
                      minWidth: '100%',
                      maxWidth: '100%',
                      fontSize: '14px',
                    },
                  },
                }}
              >
                حسناً
              </Button>
            </OKButtonWrapper>
          )}
        </Flex>
      </div>
    </Modal>
  );
};
